import { useEffect, useState } from 'react';
import { invalidateProjectionTheme } from '../charts/theme';

/**
 * Light/dark theme toggle. Flips `data-theme` on `<html>` (the design tokens re-theme via the
 * `:root[data-theme='light']` block) and invalidates the ECharts theme so charts re-register with
 * the new palette on their next mount. Terminal-DARK ships as the default; the toggle switches to the
 * light "paper terminal" alternate.
 */
type Theme = 'light' | 'dark';

const STORAGE_KEY = 'projection.theme';

function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  invalidateProjectionTheme();
}

export function ThemeToggle(): React.ReactElement {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'light' ? 'light' : 'dark';
  });

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  return (
    <button
      type="button"
      className="btn btn--ghost"
      aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
      onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))}
    >
      {theme === 'light' ? '☾' : '☀'}
    </button>
  );
}
